import React, { ReactElement } from "react";
import axios from "axios";

import { emitMigrationFlashMessageImpression } from "@catchoftheday/analytics";
import { AlertProps, Link, Span } from "@catchoftheday/cg-components";
import {
  ONEPASS_DOUBLE_SUBSCRIPTION_QUERY_PARAM_KEY,
  ONEPASS_ELIGIBLE_FOR_MIGRATION_PARAM_KEY,
  ONEPASS_MIGRATION_STATUS_QUERY_PARAM_KEY,
} from "@catchoftheday/onepass";

import {
  FLASH_BANNER_MIGRATION_ERROR_MSG,
  FLASH_BANNER_MIGRATION_SUCCESS_DOUBLE_SUB_MSG,
  FLASH_BANNER_MIGRATION_SUCCESS_MSG,
  FLASH_BANNER_NOT_ELIGIBLE_FOR_ONEPASS_MIGRATION_MSG,
} from "@header/consts";
import { getQueryStringValue } from "@header/utilities";

export type BannerProps = {
  theme: AlertProps["theme"];
  variant: AlertProps["variant"];
  showIcon?: AlertProps["showAlertIcon"];
  message: string | ReactElement;
};

export const getCountryDataMessage = async (
  isMicrofrontend = false
): Promise<BannerProps | null> => {
  if (!isMicrofrontend) {
    return null;
  }

  const {
    data: { userCountryName, shouldShopUrl },
  } = await axios.get("/country.json");

  if (userCountryName && shouldShopUrl) {
    return {
      theme: "light",
      variant: "info",
      message: (
        <Span fontSize={{ xs: "base", lg: "xl" }} m="auto">
          Hi, it looks like you're in {userCountryName}, we suggest you shop on{" "}
          <Link
            color="#000"
            textDecoration="underline"
            href={`https://${shouldShopUrl}/`}
          >
            {shouldShopUrl}
          </Link>{" "}
          instead.
        </Span>
      ),
    };
  }

  return null;
};

export const getOnePassMigrationMessage = (
  isMicrofrontend = false
): BannerProps | null => {
  const paramMigrationDoubleSubs = getQueryStringValue(
    ONEPASS_DOUBLE_SUBSCRIPTION_QUERY_PARAM_KEY
  );
  const paramMigrationStatus = getQueryStringValue(
    ONEPASS_MIGRATION_STATUS_QUERY_PARAM_KEY
  );
  const paramEligibleForMigration = getQueryStringValue(
    ONEPASS_ELIGIBLE_FOR_MIGRATION_PARAM_KEY
  );

  if (paramEligibleForMigration === "0") {
    return {
      theme: "light",
      variant: "warning",
      showIcon: true,
      message: FLASH_BANNER_NOT_ELIGIBLE_FOR_ONEPASS_MIGRATION_MSG,
    };
  }

  if (isMicrofrontend) {
    if (paramMigrationStatus === "done") {
      const msg =
        paramMigrationDoubleSubs === "1"
          ? FLASH_BANNER_MIGRATION_SUCCESS_DOUBLE_SUB_MSG
          : FLASH_BANNER_MIGRATION_SUCCESS_MSG;
      emitMigrationFlashMessageImpression({
        type: "success",
        message: msg as string,
      });
      return {
        theme: "dark",
        variant: "success",
        message: msg,
      };
    } else if (paramMigrationStatus === "error") {
      emitMigrationFlashMessageImpression({
        type: "error",
        message: FLASH_BANNER_MIGRATION_ERROR_MSG as string,
      });
      return {
        theme: "dark",
        variant: "error",
        message: FLASH_BANNER_MIGRATION_ERROR_MSG,
      };
    }
  }

  return null;
};
